/**
 * ガイドの価格表ページ（/guide/price-payment/<スラッグ>/）の、ページごとの価格表の定義
 * MEMO: 旧 js/price-payment.js の itemId・getItemPrice()・createPriceTable() と、price-payment_getItemPriceApi.js のオプション ID の ESM 版
 *       ページは <body id="price-payment-<スラッグ>">（名刺は meishi を bc に置換。設定は single-price-payment.php）
 *       商品を追加する場合は PRICE_PAGES に 1 件足す（区分・列・行見出しは下の作成関数を組み合わせる）
 */
import type { PriceRow } from '@/utils/price'

//価格表の区分（小ロット・通常ロットなど。区分ごとに商品 ID と取得するロット数が違う）
export interface PriceSection {
    label?: string  //見出し行の文字（省略時は見出し行なし）
    itemId: string
    maxLotAmount: number
    columns: string[][][]  //列ごとの商品オプション（1 件ごとのオプション ID のリスト）
    rowHeader: (row: PriceRow, index: number) => string  //行見出しの HTML
    rowFilter?: (index: number) => boolean  //表示する行（省略時はすべて）
}

export interface PricePage {
    endPoint: 'list-prices' | 'list-base-prices'
    hasDaySelect?: boolean  //発送日の選択（封筒）がある
    getSections: (day: string) => PriceSection[]  //day : 発送日の選択値（封筒のみ。通常は ''）
}

//印刷タイプ（片面白黒・両面白黒・片面カラー・片面カラー×白黒・両面カラー）
const PRINT_TYPES = ['1_0', '1_1', '4_0', '4_1', '4_4']
const SECTION_SMALL = '小ロット'
const SECTION_NORMAL = '通常ロット'

/**
 * 印刷タイプごとの列（オプション ID は OPT_PRINT_TYPE_<印刷タイプ><追加の文字列>）
 *   extraSets : 印刷タイプの後に送る商品オプション
 */
const printTypeColumns = (types: string[], suffix = '', extraSets: string[][] = []): string[][][] => {
    return types.map((type) => [[`OPT_PRINT_TYPE_${type}${suffix}`], ...extraSets])
}

/**
 * 「1セット（20枚）」形式の行見出し
 *   lotName : ロットの名前（箱・セット）、unitNum : 1 ロットの数、unitName : 数の単位（枚・部・冊）
 */
const setHeader = (lotName: string, unitNum: number, unitName: string) => (row: PriceRow, index: number): string => {
    return `${index + 1}${lotName}<span class="u-text_size-2s">（${(row.lotAmount * unitNum).toLocaleString()}${unitName}）</span>`
}

/**
 * 「100枚」形式の行見出し（封筒）
 */
const amountHeader = (unitNum: number) => (row: PriceRow): string => `${(row.lotAmount * unitNum).toLocaleString()}枚`

/**
 * 1 区分だけの価格表
 */
const singleSection = (endPoint: PricePage['endPoint'], section: PriceSection): PricePage => ({
    endPoint,
    getSections: () => [section],
})

/**
 * 小ロット（_20 の商品）と通常ロット（_100 の商品）を並べる価格表（一般名刺・カッティング）
 */
const smallAndNormalSections = (itemId: string, small: Omit<PriceSection, 'itemId' | 'columns'>, normal: Omit<PriceSection, 'itemId' | 'columns'>): PricePage => ({
    endPoint: 'list-prices',
    getSections: () => [
        { ...small, itemId: itemId.replace('_100', '_20'), columns: printTypeColumns(PRINT_TYPES) },
        { ...normal, itemId, columns: printTypeColumns(PRINT_TYPES) },
    ],
})

/**
 * 封筒（10 枚単位の _SMALL の商品と 100 枚単位の商品を並べる。印刷タイプのオプション ID に用紙・テープ・発送日を付ける）
 */
const envelopePage = (itemId: string, paper: string, types = PRINT_TYPES): PricePage => ({
    endPoint: 'list-base-prices',
    hasDaySelect: true,
    getSections: (day) => [
        { itemId: `${itemId}_SMALL`, maxLotAmount: 9, columns: printTypeColumns(types, `${paper}${day}`), rowHeader: amountHeader(10) },
        { itemId, maxLotAmount: 20, columns: printTypeColumns(types, `${paper}${day}`), rowHeader: amountHeader(100) },
    ],
})

//一般名刺（小ロットは 20 枚単位の商品）
const meishiRegular = (itemId: string): PricePage => smallAndNormalSections(itemId,
    { label: SECTION_SMALL, maxLotAmount: 4, rowHeader: setHeader('セット', 20, '枚') },
    { label: SECTION_NORMAL, maxLotAmount: 50, rowHeader: setHeader('箱', 100, '枚') },
)
//カッティング（小ロットは 1 セット 20 枚のみ）
const cutting = (itemId: string): PricePage => smallAndNormalSections(itemId,
    { label: SECTION_SMALL, maxLotAmount: 1, rowHeader: setHeader('セット', 20, '枚') },
    { label: SECTION_NORMAL, maxLotAmount: 5, rowHeader: setHeader('セット', 100, '枚') },
)
//折り名刺
const meishiOri = (itemId: string): PricePage => singleSection('list-prices', {
    itemId, maxLotAmount: 50, columns: printTypeColumns(['1_1', '4_1', '4_4']), rowHeader: setHeader('箱', 100, '枚'),
})
//私製はがき（100 セット目以降は 10 セットごと）
const hagakiShisei = (itemId: string): PricePage => singleSection('list-base-prices', {
    itemId, maxLotAmount: 300, columns: printTypeColumns(PRINT_TYPES), rowHeader: setHeader('セット', 10, '枚'),
    rowFilter: (index) => index<99 || (index - 99) % 10===0,
})
//封筒付きカード（列は用紙。ケント・大礼）
const futoCard = (itemId: string, papers: string[]): PricePage => singleSection('list-base-prices', {
    itemId, maxLotAmount: 30, columns: papers.map((paper) => [[paper]]), rowHeader: setHeader('セット', 10, '枚'),
})
//チケット
const ticket = (itemId: string): PricePage => singleSection('list-prices', {
    itemId, maxLotAmount: 50, columns: printTypeColumns(PRINT_TYPES), rowHeader: setHeader('セット', 100, '枚'),
})
//チラシ
const flyer = (itemId: string): PricePage => singleSection('list-base-prices', {
    itemId, maxLotAmount: 30, columns: printTypeColumns(PRINT_TYPES), rowHeader: setHeader('セット', 10, '部'),
})
//マスクケース
const maskCase = (itemId: string, types: string[]): PricePage => singleSection('list-base-prices', {
    itemId, maxLotAmount: 30, columns: printTypeColumns(types), rowHeader: setHeader('セット', 10, '部'),
})
//活版印刷（列は用紙グループ。印刷タイプは 1 色で取得）
const letterpress = (itemId: string, lotName: string): PricePage => singleSection('list-base-prices', {
    itemId, maxLotAmount: 10,
    columns: ['OPT_PAPER_0067', 'OPT_PAPER_0069', 'OPT_PAPER_0074'].map((paper) => [[paper], ['OPT_PRINT_TYPE_DIC_11']]),
    rowHeader: setHeader(lotName, 100, '枚'),
})

//body の id ごとの価格表の定義
export const PRICE_PAGES: Record<string, PricePage> = {
  //一般名刺
    'price-payment-bc-regular': meishiRegular('ITM_MEISHI_REGULAR_100'),
    'price-payment-bc-dansai': meishiRegular('ITM_MEISHI_OUBEI_100'),
  //折り名刺
    'price-payment-bc-2ori': meishiOri('ITM_MEISHI_2ORI'),
    'price-payment-bc-2ori-henkei': meishiOri('ITM_MEISHI_2ORI_HENKEI'),
    'price-payment-bc-3ori': meishiOri('ITM_MEISHI_3ORI'),
    'price-payment-bc-3ori-henkei': meishiOri('ITM_MEISHI_3ORI_HENKEI'),
    'price-payment-bc-4ori': meishiOri('ITM_MEISHI_4ORI'),
    'price-payment-bc-4ori-henkei': meishiOri('ITM_MEISHI_4ORI_HENKEI'),
  //台紙持込
    'price-payment-bc-daishi': singleSection('list-prices', {
        itemId: 'ITM_MEISHI_DAISHI', maxLotAmount: 50, columns: printTypeColumns(['b_0', 'b_b']), rowHeader: setHeader('箱', 100, '枚'),
    }),
  //はがき
    'price-payment-hagaki-shisei': hagakiShisei('ITM_HAGAKI_SHISEI'),
    'price-payment-hagaki-shisei-henkei': hagakiShisei('ITM_HAGAKI_SHISEI_HENKEI'),
    'price-payment-hagaki-shisei-large': hagakiShisei('ITM_HAGAKI_SHISEI_LARGE'),
  //官製はがき（用紙は 0056 固定。5 セット目から表示）
    'price-payment-hagaki-kansei': singleSection('list-base-prices', {
        itemId: 'ITM_HAGAKI_KANSEI', maxLotAmount: 250, columns: printTypeColumns(['1_0', '4_0'], '', [['OPT_PAPER_0056']]),
        rowHeader: setHeader('セット', 4, '枚'), rowFilter: (index) => index>=4,
    }),
  //封筒付きカード
    'price-payment-futo-card-s': futoCard('ITM_FUTO_CARD', ['OPT_PAPER_0052', 'OPT_PAPER_0053']),
    'price-payment-futo-card-w': futoCard('ITM_FUTO_CARD_2ORI', ['OPT_PAPER_0054', 'OPT_PAPER_0055']),
  //チケット（ticket-dansai のページは ticket-henkei）
    'price-payment-ticket-normal': ticket('ITM_TICKET'),
    'price-payment-ticket-henkei': ticket('ITM_TICKET_HENKEI'),
    'price-payment-ticket-large': ticket('ITM_TICKET_LARGE'),
  //チラシ
    'price-payment-flyer-a3': flyer('ITM_FLYER_A3'),
    'price-payment-flyer-a4': flyer('ITM_FLYER_A4'),
    'price-payment-flyer-b4': flyer('ITM_FLYER_B4'),
    'price-payment-flyer-b5': flyer('ITM_FLYER_B5'),
    'price-payment-flyer-a3-2ori': flyer('ITM_FLYER_A3_2ORI'),
    'price-payment-flyer-a4-2ori': flyer('ITM_FLYER_A4_2ORI'),
    'price-payment-flyer-b4-2ori': flyer('ITM_FLYER_B4_2ORI'),
    'price-payment-flyer-b5-2ori': flyer('ITM_FLYER_B5_2ORI'),
    'price-payment-flyer-a3-3ori': flyer('ITM_FLYER_A3_3ORI'),
    'price-payment-flyer-a4-3ori': flyer('ITM_FLYER_A4_3ORI'),
    'price-payment-flyer-b4-3ori': flyer('ITM_FLYER_B4_3ORI'),
    'price-payment-flyer-b5-3ori': flyer('ITM_FLYER_B5_3ORI'),
  //マスクケース
    'price-payment-mask-case-single': maskCase('ITM_LIMEX_MASK_CASE_SINGLE', ['1_0', '4_0']),
    'price-payment-mask-case-double': maskCase('ITM_LIMEX_MASK_CASE_DOUBLE', PRINT_TYPES),
  //カッティング
    'price-payment-cutting-91_55': cutting('ITM_CUTTING_9155_100'),
    'price-payment-cutting-91_110': cutting('ITM_CUTTING_91110_100'),
    'price-payment-cutting-91_165': cutting('ITM_CUTTING_91165_100'),
    'price-payment-cutting-182_55': cutting('ITM_CUTTING_18255_100'),
    'price-payment-cutting-273_55': cutting('ITM_CUTTING_27355_100'),
  //活版印刷
    'price-payment-letterpress-meishi': letterpress('ITM_MEISHI_LETTERPRESS', '箱'),
    'price-payment-letterpress-hagaki': letterpress('ITM_HAGAKI_LETTERPRESS', 'セット'),
    'price-payment-letterpress-meishi-both': letterpress('ITM_MEISHI_LETTERPRESS_RYOUMEN', '箱'),
    'price-payment-letterpress-hagaki-both': letterpress('ITM_HAGAKI_LETTERPRESS_RYOUMEN', 'セット'),
  //卓上カレンダー（小ロットは 10 冊単位の商品。列は片面・両面印刷）
    'price-payment-calendar-desktop-b6': {
        endPoint: 'list-base-prices',
        getSections: () => [
            { label: SECTION_SMALL, itemId: 'ITM_CALENDAR_DESKTOP_ECO_B6_10', maxLotAmount: 9, rowHeader: setHeader('セット', 10, '冊'), columns: [] },
            { label: SECTION_NORMAL, itemId: 'ITM_CALENDAR_DESKTOP_ECO_B6_100', maxLotAmount: 3, rowHeader: setHeader('セット', 100, '冊'), columns: [] },
        ].map((section) => ({
            ...section,
            columns: ['OPT_PRINT_TYPE_CALENDAR_BACK', 'OPT_PRINT_TYPE_CALENDAR_BOTH'].map((type) => [['OPT_CALENDAR_ITEM_TYPE_TEMPLATE'], [type]]),
        })),
    },
  //お名前シール
    'price-payment-name-seal': singleSection('list-prices', {
        itemId: 'ITM_NAME_SEAL_DATA', maxLotAmount: 30, columns: [[['OPT_NONE']]], rowHeader: (row) => `${row.lotAmount.toLocaleString()}シート`,
    }),
  //封筒（ケント・クラフト・パステル、テープ有無）
    'price-payment-envelope-332_240-kent': envelopePage('ITM_ENVELOPE_332_240', '_KENT'),
    'price-payment-envelope-332_240-kent-tape': envelopePage('ITM_ENVELOPE_332_240', '_KENT_TAPE'),
    'price-payment-envelope-332_240-craft': envelopePage('ITM_ENVELOPE_332_240', '_CRAFT'),
    'price-payment-envelope-332_240-craft-tape': envelopePage('ITM_ENVELOPE_332_240', '_CRAFT_TAPE'),
    'price-payment-envelope-332_240-pastel': envelopePage('ITM_ENVELOPE_332_240', '_PASTEL'),
    'price-payment-envelope-332_240-pastel-tape': envelopePage('ITM_ENVELOPE_332_240', '_PASTEL_TAPE'),
    'price-payment-envelope-235_120-kent': envelopePage('ITM_ENVELOPE_235_120', '_KENT'),
    'price-payment-envelope-235_120-kent-tape': envelopePage('ITM_ENVELOPE_235_120', '_KENT_TAPE'),
    'price-payment-envelope-235_120-craft': envelopePage('ITM_ENVELOPE_235_120', '_CRAFT'),
    'price-payment-envelope-235_120-craft-tape': envelopePage('ITM_ENVELOPE_235_120', '_CRAFT_TAPE'),
    'price-payment-envelope-235_120-pastel': envelopePage('ITM_ENVELOPE_235_120', '_PASTEL'),
    'price-payment-envelope-235_120-pastel-tape': envelopePage('ITM_ENVELOPE_235_120', '_PASTEL_TAPE'),
  //窓付き封筒は片面印刷のみ
    'price-payment-envelope-235_120_mado-kent': envelopePage('ITM_ENVELOPE_235_120_mado', '_KENT', ['1_0', '4_0']),
    'price-payment-envelope-235_120_mado-craft': envelopePage('ITM_ENVELOPE_235_120_mado', '_CRAFT', ['1_0', '4_0']),
    'price-payment-envelope-235_120_mado-pastel': envelopePage('ITM_ENVELOPE_235_120_mado', '_PASTEL', ['1_0', '4_0']),
    'price-payment-envelope-235_120_you-kent': envelopePage('ITM_ENVELOPE_235_120_you', '_KENT'),
    'price-payment-envelope-235_120_you-pastel': envelopePage('ITM_ENVELOPE_235_120_you', '_PASTEL'),
}
